import React, { useState, useEffect } from 'react';
import { useOnboardingStore } from '../../store';
import { ArrowLeft, Loader2, FastForward } from 'lucide-react';
import { canSkipSteps } from '../../config/beta';
import {
  loadServiceSubscriptionAvailability,
  serviceIsUnavailable,
  serviceUnavailableMessage,
  type ServiceSubscriptionAvailability,
} from '../../lib/serviceAvailability';

export function Costs() {
  const {
    setStep,
    selectedServiceTypes,
    selectedServices
  } = useOnboardingStore();
  const [isLoading, setIsLoading] = useState(false);
  const [availabilityRows, setAvailabilityRows] = useState<ServiceSubscriptionAvailability[]>([]);

  useEffect(() => {
    let active = true;
    loadServiceSubscriptionAvailability()
      .then((rows) => {
        if (active) setAvailabilityRows(rows);
      })
      .catch(() => {
        if (active) setAvailabilityRows([]);
      });
    return () => { active = false; };
  }, []);

  // Validate current step and selections
  useEffect(() => {
    // Redirect if no valid selections
    if (selectedServiceTypes.length === 0) {
      setStep(2);
      return;
    }
  }, [selectedServiceTypes, setStep]);

  const availableSelectedServices = selectedServices.filter((service) => {
    const serviceTypeId = serviceTypeForSelectedService(service.id, service.name);
    return !serviceIsUnavailable(availabilityRows, serviceTypeId);
  });
  const unavailableSelectedServices = selectedServices.filter((service) => {
    const serviceTypeId = serviceTypeForSelectedService(service.id, service.name);
    return serviceIsUnavailable(availabilityRows, serviceTypeId);
  });

  // Calculate total cost
  const calculateTotalCost = () => {
    let total = 0;

    // Add only monthly-billed services
    if (availableSelectedServices && availableSelectedServices.length > 0) {
      total = availableSelectedServices.reduce((sum, service) => {
        // Only include monthly plans (not annual)
        if (!service.id.includes('-annual')) {
          return sum + service.monthlyFee;
        }
        return sum;
      }, 0);
    }

    return total;
  };

  // Calculate total initial/setup costs
  const calculateInitialCost = () => {
    let total = 0;

    // Add setup costs from selected services
    if (availableSelectedServices && availableSelectedServices.length > 0) {
      total = availableSelectedServices.reduce((sum, service) => sum + (service.initialCost || 0), 0);
    }

    return total;
  };

  // Calculate total annual cost
  const calculateAnnualCost = () => {
    let total = 0;

    // Add only annual-billed services
    if (availableSelectedServices && availableSelectedServices.length > 0) {
      total = availableSelectedServices.reduce((sum, service) => {
        // Only include annual plans
        if (service.id.includes('-annual')) {
          return sum + (service.monthlyFee * 12);
        }
        return sum;
      }, 0);
    }

    return total;
  };

  // Check service selections
  const totalAmount = calculateTotalCost();
  const totalInitialCost = calculateInitialCost();
  const totalAnnualCost = calculateAnnualCost();

  const handleBack = () => {
    setStep(3);
  };

  const handleSkip = () => {
    if (canSkipSteps()) {
      setStep(5);
    }
  };

  const handleContinue = async () => {
    try {
      setIsLoading(true);

      setStep(5);

    } catch (error) {
      console.error('Continue error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonText = () => {
    if (isLoading) {
      return (
        <>
          <Loader2 className="w-4 h-4 mr-2 inline animate-spin" />
          Continuing...
        </>
      );
    }

    return 'Continue to Connections';
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold text-white mb-6">Review Your Services</h2>
      
      <p className="text-gray-300 mb-8">
        Review your selected services and their costs below. No payment is taken during onboarding.
      </p>
      
      <div className="bg-scale-darker bg-opacity-50 backdrop-blur-sm border border-gray-700 p-6 rounded-lg mb-8">
        <h3 className="text-lg font-medium text-white mb-4">Selected Services</h3>

        {/* All Selected Services */}
        {availableSelectedServices && availableSelectedServices.length > 0 && availableSelectedServices.map((service) => {
          const isAnnual = service.id.includes('-annual');
          const annualTotal = isAnnual ? service.monthlyFee * 12 : null;

          return (
            <div key={service.id} className="mb-4 p-4 bg-scale-blue/10 border border-scale-blue/20 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-scale-blue font-medium">{service.name}</span>
                <div className="text-right">
                  <span className="font-semibold text-white">${service.monthlyFee}/month</span>
                  {isAnnual && annualTotal && (
                    <div className="text-xs text-gray-400 mt-1">
                      ${annualTotal}/year billed annually
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-300">{service.description}</p>
            </div>
          );
        })}

        {unavailableSelectedServices.map((service) => {
          const serviceTypeId = serviceTypeForSelectedService(service.id, service.name);
          return (
            <div key={service.id} className="mb-4 p-4 bg-gray-800/30 border border-gray-700 rounded-lg opacity-70">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-300 font-medium">{service.name}</span>
                <span className="font-semibold text-gray-400">Currently unavailable</span>
              </div>
              <p className="text-sm text-gray-400">{serviceUnavailableMessage(availabilityRows, serviceTypeId)}</p>
            </div>
          );
        })}


        {/* Totals */}
        {(totalAmount > 0 || totalAnnualCost > 0 || totalInitialCost > 0) && (
          <div className="border-t border-gray-700 pt-4 mt-4 space-y-2">
            {totalAmount > 0 && (
              <div className="flex justify-between">
                <span className="font-medium text-white">Total Monthly Cost</span>
                <span className="font-bold text-scale-blue">
                  ${totalAmount}/month
                </span>
              </div>
            )}
            {totalAnnualCost > 0 && (
              <div className="flex justify-between">
                <span className="font-medium text-white">Total Annual Cost</span>
                <span className="font-bold text-scale-blue">
                  ${totalAnnualCost}/year
                </span>
              </div>
            )}
            {totalInitialCost > 0 && (
              <div className="flex justify-between">
                <span className="font-medium text-white">Initial Setup Cost</span>
                <span className="font-bold text-scale-blue">
                  ${totalInitialCost}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center space-x-4 pt-4">
        <button
          onClick={handleBack}
          className="w-32 flex items-center justify-center px-6 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors font-semibold"
          disabled={isLoading}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
        <div className="flex gap-3">
          {canSkipSteps() && (
            <button
              onClick={handleSkip}
              className="flex items-center bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors font-medium"
            >
              <FastForward className="w-4 h-4 mr-2" />
              Skip (Beta)
            </button>
          )}
          <button
            onClick={handleContinue}
            disabled={isLoading || availableSelectedServices.length === 0}
            className="bg-scale-blue text-scale-dark font-semibold px-6 py-2 rounded-md hover:bg-white focus:outline-none focus:ring-2 focus:ring-scale-blue focus:ring-offset-2 focus:ring-offset-scale-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {getButtonText()}
          </button>
        </div>
      </div>

    </div>
  );
}

function serviceTypeForSelectedService(id: string, name: string) {
  const value = `${id} ${name}`.toLowerCase();
  if (value.includes('repeat-referral')) return 'repeat-referral-system';
  if (value.includes('content-engine')) return 'content-engine';
  if (value.includes('proof-of-work')) return 'proof-of-work';
  if (value.includes('local-authority')) return 'nap-entity-boost';
  if (value.includes('ai-audit') || value.includes('ai audit')) return 'ai-audit';
  if (value.includes('seo-gbp') || value.includes('seo & gbp')) return 'seo-gbp-audit';
  if (value.includes('customer-intelligence')) return 'customer-intelligence';
  return '';
}
